import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { flows, responses } from '../../services/api';
import { captureCourseRuntimeContext, CapturedCourseContext } from 'services/api/courseRuntimeContext';
import { getAuthGeneration } from 'utils/auth';
import type { FlowSubmissionRequest, FlowSubmissionResult } from 'services/api/flowsClient';
import { Flow, FlowRun, Question, ResponsePayload } from '../../types';
import { isQuestionResponseValid } from 'utils/questionResponseValidation';
import { normalizeQuestionType } from 'utils/questionTypeUtils';
import { useLatestRef } from '../../hooks/useLatestRef';
import { cloneMetadata } from 'utils/responseNormalization';
import {
  FlowResponseDraft,
  StoredResponse,
  areResponsesEqual,
  buildRequestPayload,
  mapApiResponseToStored,
  storedToDraft,
} from './responseUtils';
import {
  applyStoredResponse,
  deriveDraftFromOverride,
  getErrorMessage,
  getWorkingDraft,
  shouldDeferIncompleteCustomOtherPersist,
  shouldSkipPersist,
} from './questionSessionHelpers';
import { QuestionFlowInitialBootstrap } from './types';
import { hasAnswerEvaluation, isSkippedAnswerEvaluation } from 'utils/answerEvaluation';
import { hasQuestionGeneration } from 'utils/questionGeneration';
import { hasAnswerReferences } from './useAnswerReferences';
import { isQuestionGenerationConflict } from 'services/api/questionGenerationClient';

export {
  applyStoredResponse,
  deriveDraftFromOverride,
  getWorkingDraft,
  hasUserModifiedDraft,
  shouldDeferIncompleteCustomOtherPersist,
  shouldForceComplete,
  shouldSkipPersist,
} from './questionSessionHelpers';

interface FlowUser {
  id: number;
}

interface UseQuestionFlowManagerParams {
  flowName: string;
  initialFlow?: Flow | null;
  initialBootstrap?: QuestionFlowInitialBootstrap | null;
  user: FlowUser | null;
  targetDate?: string;
  initialFlowRunId?: number;
  /** The course the flow was opened from; picks the course theme the flow inherits. */
  courseId?: number | null;
}

interface SaveScope {
  userId: number | undefined;
  authGeneration: number;
  savedResponses: Record<number, StoredResponse>;
  pendingAnswers: Map<number, AnswerSaveSnapshot>;
  retrySubmission?: () => Promise<void>;
}

const reconcileBeforeAutosave = async (owner: SaveScope): Promise<void> => {
  try {
    await owner.retrySubmission?.();
  } catch (failure) {
    // Definitive rejections clear the retry slot. Ordinary autosaves may then
    // use their existing server guards; ambiguous receipts must resolve first.
    if (owner.retrySubmission) throw failure;
  }
};

interface AnswerSaveSnapshot {
  owner: SaveScope;
  questionId: number;
  questionType: string;
  payload: ResponsePayload;
  context: CapturedCourseContext;
}

interface DebouncedSaveEntry {
  timeoutId: ReturnType<typeof setTimeout> | null;
  promise: Promise<void>;
  resolve: () => void;
  reject: (error: unknown) => void;
  snapshot: AnswerSaveSnapshot;
  previousSave?: Promise<void>;
}

export type SubmitQuestionOptions = {
  advance?: boolean;
  persistImmediately?: boolean;
  /** The evaluation endpoint already persisted this canonical response. */
  serverPersisted?: boolean;
  propagateErrors?: boolean;
  isCurrentSubmission?: () => boolean;
};

export interface SubmitFlowOptions {
  preserveQuestionOnError?: boolean;
  finalResponse?: ResponsePayload;
  serverPersisted?: boolean;
  isCurrentSubmission?: () => boolean;
}

const newSubmissionId = (): string => {
  // UUIDs are operation identities, not credentials. Browser crypto also works
  // when randomUUID is unavailable in an older supported browser.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const AUTO_SAVE_DEBOUNCE_MS = 750;
/** Question types that generate their content from earlier saved answers as soon as they mount. */
const GENERATED_QUESTION_TYPES = new Set(['ai_summary', 'callback_summary']);

type FlowRunStatus = 'not_started' | 'in_progress' | 'completed';

interface FlowRunState {
  currentIndex: number;
  totalQuestions: number;
  completedAt: string | null;
  status: FlowRunStatus;
  flowRunId: number | null;
  targetDate: string | null;
  progressPercent: number;
  completedQuestionCount: number;
  isFullyAnswered: boolean;
  firstIncompleteIndex: number | null;
}

interface UseQuestionFlowManagerResult {
  loading: boolean;
  error: string | null;
  isAccessDenied: boolean;
  lockedCourseId: number | null;
  flow: Flow | null;
  questions: Question[];
  currentQuestionIndex: number;
  currentQuestion: Question | null;
  responsesByQuestion: Record<number, FlowResponseDraft>;
  /** Completed saves per question; answer references refresh when another question's count changes. */
  persistedRevisions: Record<number, number>;
  orderedResponses: FlowResponseDraft[];
  hasUnsavedChanges: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  canSubmit: boolean;
  isFlowCompleted: boolean;
  progress: FlowRunState;
  goToNextQuestion: () => void;
  goToPreviousQuestion: () => void;
  goToQuestion: (index: number) => void;
  submitQuestionResponse: (
    response: ResponsePayload,
    options?: SubmitQuestionOptions,
  ) => Promise<void | FlowRunState | undefined>;
  saveProgress: () => Promise<void>;
  submitFlow: (options?: SubmitFlowOptions) => Promise<void>;
  submissionResult: FlowSubmissionResult | null;
  adoptServerResponses: (rawResponses: unknown[]) => void;
}

interface HydratedQuestionFlowState {
  flowState: Flow | null;
  questionsState: Question[];
  currentIndex: number;
  flowRunState: FlowRunState;
  responseDrafts: Record<number, FlowResponseDraft>;
  serverResponses: Record<number, StoredResponse>;
  flowItemByQuestion: Record<number, number>;
}

const clampIndex = (value: unknown, totalQuestions: number): number | null => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return null;
  }

  return Math.min(Math.max(value, 0), totalQuestions);
};

const deriveProgressPercent = (completedQuestionCount: number, totalQuestions: number): number => {
  if (totalQuestions === 0) {
    return 100;
  }

  return Math.round((completedQuestionCount / totalQuestions) * 10000) / 100;
};

const countCompletedQuestions = (
  questions: Question[],
  drafts: Record<number, FlowResponseDraft>,
): number =>
  questions.filter((question) => isQuestionCompleteFromDraft(question, drafts)).length;

const isQuestionCompleteFromDraft = (
  question: Question,
  drafts: Record<number, FlowResponseDraft>,
): boolean => {
    const questionId = question.id;
    if (typeof questionId !== 'number') {
      return false;
    }

    const draft = drafts[questionId];
    if (!draft || draft.question !== questionId || draft.is_completed !== true) {
      return false;
    }

    return isSkippedAnswerEvaluation(question, draft) || isQuestionResponseValid(question, { draft });
};

const findFirstIncompleteQuestionIndex = (
  questions: Question[],
  drafts: Record<number, FlowResponseDraft>,
): number => questions.findIndex((question) => !isQuestionCompleteFromDraft(question, drafts));

const buildFlowRunState = ({
  flowRunData,
  totalQuestions,
  fallbackIndex,
  fallbackCompletedAt,
  fallbackStatus,
  fallbackFlowRunId,
  fallbackTargetDate,
  fallbackCompletedQuestionCount,
  fallbackFirstIncompleteIndex,
}: {
  flowRunData: Partial<FlowRun> | null;
  totalQuestions: number;
  fallbackIndex: number;
  fallbackCompletedAt: string | null;
  fallbackStatus: FlowRunStatus;
  fallbackFlowRunId: number | null;
  fallbackTargetDate: string | null;
  fallbackCompletedQuestionCount: number;
  fallbackFirstIncompleteIndex: number | null;
}): FlowRunState => {
  const currentIndex =
    clampIndex(flowRunData?.current_question_index ?? flowRunData?.current_index, totalQuestions) ??
    fallbackIndex;
  const completedQuestionCountRaw = flowRunData?.completed_question_count;
  const completedQuestionCount =
    typeof completedQuestionCountRaw === 'number' && !Number.isNaN(completedQuestionCountRaw)
      ? Math.min(Math.max(Math.round(completedQuestionCountRaw), 0), totalQuestions)
      : fallbackCompletedQuestionCount;
  const firstIncompleteIndexValue = flowRunData?.first_incomplete_index;
  const firstIncompleteIndex =
    firstIncompleteIndexValue === null
      ? null
      : clampIndex(firstIncompleteIndexValue, totalQuestions) ?? fallbackFirstIncompleteIndex;
  const isFullyAnswered =
    typeof flowRunData?.is_fully_answered === 'boolean'
      ? flowRunData.is_fully_answered
      : completedQuestionCount >= totalQuestions;
  const progressPercentRaw = flowRunData?.progress_percent;
  const progressPercent =
    typeof progressPercentRaw === 'number' && !Number.isNaN(progressPercentRaw)
      ? Math.min(Math.max(progressPercentRaw, 0), 100)
      : deriveProgressPercent(completedQuestionCount, totalQuestions);
  const completedAt = typeof flowRunData?.completed_at === 'string' ? flowRunData.completed_at : fallbackCompletedAt;
  const status: FlowRunStatus =
    flowRunData?.status === 'completed'
      ? 'completed'
      : isFullyAnswered
        ? 'completed'
        : currentIndex > 0 || completedQuestionCount > 0
          ? 'in_progress'
          : fallbackStatus;
  const flowRunId =
    typeof flowRunData?.flow_run_id === 'number' ? flowRunData.flow_run_id : fallbackFlowRunId;
  const targetDate =
    typeof flowRunData?.target_date === 'string' ? flowRunData.target_date : fallbackTargetDate;

  return {
    currentIndex: status === 'completed' ? totalQuestions : currentIndex,
    totalQuestions,
    completedAt: status === 'completed' ? completedAt : null,
    status,
    flowRunId,
    targetDate,
    progressPercent,
    completedQuestionCount,
    isFullyAnswered,
    firstIncompleteIndex,
  };
};

const buildHydratedQuestionFlowState = ({
  flow,
  questions,
  flowRunData,
  rawResponses,
  targetDate,
  initialFlowRunId,
}: {
  flow: Flow | null;
  questions: Question[];
  flowRunData: Partial<FlowRun> | null;
  rawResponses: ResponsePayload[];
  targetDate?: string;
  initialFlowRunId?: number;
}): HydratedQuestionFlowState => {
  const resolvedTargetDate =
    typeof flowRunData?.target_date === 'string'
      ? flowRunData.target_date
      : targetDate ?? null;
  const activeFlowRunId =
    typeof flowRunData?.flow_run_id === 'number'
      ? flowRunData.flow_run_id
      : typeof flowRunData?.id === 'number'
        ? flowRunData.id
        : typeof initialFlowRunId === 'number'
          ? initialFlowRunId
          : null;
  const questionList: Question[] = Array.isArray(questions) ? questions : [];
  const flowItemMap: Record<number, number> = {};
  questionList.forEach((question) => {
    if (typeof question?.id === 'number' && typeof question?.flow_item_id === 'number') {
      flowItemMap[question.id] = question.flow_item_id;
    }
  });

  const questionIds = questionList
    .map((question) => question.id)
    .filter((id): id is number => typeof id === 'number');
  const latestByQuestion: Record<number, StoredResponse> = {};
  rawResponses.forEach((rawResponse) => {
    const mapped = mapApiResponseToStored(rawResponse);
    if (!mapped || !questionIds.includes(mapped.question)) {
      return;
    }

    if (!(mapped.question in latestByQuestion)) {
      latestByQuestion[mapped.question] = mapped;
    }
  });

  const drafts: Record<number, FlowResponseDraft> = {};
  Object.keys(latestByQuestion).forEach((key) => {
    const questionId = Number(key);
    const stored = latestByQuestion[questionId];
    if (!stored) {
      return;
    }
    drafts[questionId] = storedToDraft(stored);
  });

  const totalQuestions = questionList.length;
  const firstIncompleteIndex = findFirstIncompleteQuestionIndex(questionList, drafts);
  const derivedIndex = firstIncompleteIndex === -1 ? totalQuestions : firstIncompleteIndex;
  const derivedCompletedQuestionCount = countCompletedQuestions(questionList, drafts);
  const savedProgressRaw =
    typeof flowRunData?.current_index === 'number'
      ? flowRunData.current_index
      : typeof flowRunData?.current_question_index === 'number'
        ? flowRunData.current_question_index
        : null;
  const savedProgressIndex =
    savedProgressRaw !== null
      ? Math.min(Math.max(savedProgressRaw, 0), totalQuestions)
      : null;
  const defaultIndex =
    savedProgressIndex !== null ? Math.min(derivedIndex, savedProgressIndex) : derivedIndex;
  const fallbackFirstIncompleteIndex = firstIncompleteIndex === -1 ? null : firstIncompleteIndex;
  const nextFlowRunState = buildFlowRunState({
    flowRunData,
    totalQuestions,
    fallbackIndex: savedProgressIndex ?? defaultIndex,
    fallbackCompletedAt: flowRunData?.completed_at ?? null,
    fallbackStatus: (savedProgressIndex ?? derivedCompletedQuestionCount) > 0 ? 'in_progress' : 'not_started',
    fallbackFlowRunId: activeFlowRunId,
    fallbackTargetDate: resolvedTargetDate,
    fallbackCompletedQuestionCount: derivedCompletedQuestionCount,
    fallbackFirstIncompleteIndex,
  });
  const completionIndex = nextFlowRunState.status === 'completed'
    ? totalQuestions
    : nextFlowRunState.firstIncompleteIndex ?? defaultIndex;

  return {
    flowState: flow,
    questionsState: questionList,
    currentIndex: completionIndex,
    flowRunState: nextFlowRunState,
    responseDrafts: drafts,
    serverResponses: latestByQuestion,
    flowItemByQuestion: flowItemMap,
  };
};

// Main hook that orchestrates loading, answering, saving, and submitting a question flow.
export const useQuestionFlowManager = ({
  flowName,
  initialFlow = null,
  initialBootstrap = null,
  user,
  targetDate,
  initialFlowRunId,
  courseId = null,
}: UseQuestionFlowManagerParams): UseQuestionFlowManagerResult => {
  const hydratedBootstrapState = useMemo(
    () =>
      initialBootstrap
        ? buildHydratedQuestionFlowState({
            flow: initialBootstrap.flow,
            questions: initialBootstrap.questions,
            flowRunData: initialBootstrap.flowRun,
            rawResponses: initialBootstrap.responses,
            targetDate,
            initialFlowRunId,
          })
        : null,
    [initialBootstrap, initialFlowRunId, targetDate],
  );
  const [loading, setLoading] = useState(() => !hydratedBootstrapState);
  const [error, setError] = useState<string | null>(null);
  const [isAccessDenied, setIsAccessDenied] = useState(false);
  const [lockedCourseId, setLockedCourseId] = useState<number | null>(null);
  useEffect(() => setLockedCourseId(null), [flowName]);
  const handleRuntimeError = useCallback((runtimeError: unknown): never => {
    const response = (runtimeError as {
      response?: { status?: number; data?: { code?: string; reason?: string; course_id?: number } };
    })?.response;
    const data = response?.data;
    if (response?.status === 403
      && (data?.code === 'course_prerequisites_unmet' || data?.reason === 'course_prerequisites_unmet')
      && typeof data.course_id === 'number' && Number.isSafeInteger(data.course_id) && data.course_id > 0) {
      setLockedCourseId(data.course_id);
    }
    throw runtimeError;
  }, []);

  const [flowState, setFlowState] = useState<Flow | null>(
    () => hydratedBootstrapState?.flowState ?? initialFlow,
  );
  const [questionsState, setQuestionsState] = useState<Question[]>(
    () => hydratedBootstrapState?.questionsState ?? [],
  );
  const [currentIndex, setCurrentIndex] = useState<number>(
    () => hydratedBootstrapState?.currentIndex ?? 0,
  );
  const [flowRunState, setFlowRunState] = useState<FlowRunState>(
    () =>
      hydratedBootstrapState?.flowRunState ?? {
        currentIndex: 0,
        totalQuestions: 0,
        completedAt: null,
        status: 'not_started',
        flowRunId: null,
        targetDate: null,
        progressPercent: 0,
        completedQuestionCount: 0,
        isFullyAnswered: false,
        firstIncompleteIndex: null,
      },
  );
  const [responseDrafts, setResponseDrafts] = useState<Record<number, FlowResponseDraft>>(
    () => hydratedBootstrapState?.responseDrafts ?? {},
  );
  const responseDraftsRef = useLatestRef<Record<number, FlowResponseDraft>>(responseDrafts);
  const [serverResponses, setServerResponses] = useState<Record<number, StoredResponse>>(
    () => hydratedBootstrapState?.serverResponses ?? {},
  );
  const serverResponsesRef = useLatestRef<Record<number, StoredResponse>>(serverResponses);
  const [flowItemByQuestion, setFlowItemByQuestion] = useState<Record<number, number>>(
    () => hydratedBootstrapState?.flowItemByQuestion ?? {},
  );
  const draftRevisionRef = useRef(0);
  const backgroundSavesRef = useRef<Record<number, Promise<void>>>({});
  // REQ-106: which answers last failed to save, and a per-question count of
  // completed saves so answer references refresh once an edit is persisted.
  const failedBackgroundSavesRef = useRef<Set<number>>(new Set());
  const [persistedRevisions, setPersistedRevisions] = useState<Record<number, number>>({});
  const debouncedSavesRef = useRef<Record<number, DebouncedSaveEntry>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const initialFlowRef = useLatestRef(initialBootstrap?.flow ?? initialFlow);
  const scope = useMemo(() => ({
    courseId, flowName, initialFlowRunId, targetDate, userId: user?.id, runId: flowRunState.flowRunId,
    authGeneration: getAuthGeneration(), savedResponses: {} as Record<number, StoredResponse>,
    pendingAnswers: new Map<number, AnswerSaveSnapshot>(), retrySubmission: undefined as SaveScope['retrySubmission'],
  }),
    [courseId, flowName, initialFlowRunId, targetDate, user?.id, flowRunState.flowRunId]);
  const currentUserIdRef = useRef(user?.id);
  currentUserIdRef.current = user?.id;
  const scopeRef = useRef<SaveScope | null>(scope);
  scopeRef.current = scope;
  const submissionRef = useRef<{ scope: object; promise: Promise<void> } | null>(null);
  const retryRef = useRef<{ scope: object; signature: string; request: FlowSubmissionRequest } | null>(null);
  const pendingProgressRef = useRef(new Set<Promise<unknown>>());
  const pendingBulkSavesRef = useRef(new Set<Promise<unknown>>());
  const pendingSaveWritesRef = useRef(new Set<Promise<void>>());
  const [submissionState, setSubmissionState] = useState<{ scope: object; result: FlowSubmissionResult } | null>(null);
  const submissionResult = submissionState?.scope === scope ? submissionState.result : null;
  const isFinishing = useCallback(() => submissionRef.current?.scope === scopeRef.current, []);




  useEffect(() => {
    if (hydratedBootstrapState) {
      setLoading(false);
      setError(null);
      setIsAccessDenied(false);
      setFlowState(hydratedBootstrapState.flowState);
      setQuestionsState(hydratedBootstrapState.questionsState);
      setCurrentIndex(hydratedBootstrapState.currentIndex);
      setFlowRunState(hydratedBootstrapState.flowRunState);
      setResponseDrafts(hydratedBootstrapState.responseDrafts);
      responseDraftsRef.current = hydratedBootstrapState.responseDrafts;
      setServerResponses(hydratedBootstrapState.serverResponses);
      serverResponsesRef.current = hydratedBootstrapState.serverResponses;
      setFlowItemByQuestion(hydratedBootstrapState.flowItemByQuestion);
      return;
    }

    if (!flowName || !user?.id) {
      setLoading(false);
      return;
    }

    let isCancelled = false;

    // Bootstrap flow metadata, questions, progress, and prior responses.
    const initialize = async () => {
      setLoading(true);
      setError(null);

      try {
        const flowIdentifier = initialFlowRef.current?.id ?? flowName;

        const [flow, questions, flowRun] = await Promise.all([
          flows.getFlow(flowName, { courseId }),
          flows.getQuestions(flowIdentifier, { filtered: true }),
          flows.getFlowRun(flowName, {
            targetDate,
            flowRunId: initialFlowRunId,
          }),
        ]);

        if (isCancelled) {
          return;
        }

        const remoteFlow = flow?.data ?? null;
        const flowRunData = flowRun?.data ?? null;
        const resolvedTargetDate =
          typeof flowRunData?.target_date === 'string'
            ? flowRunData.target_date
            : targetDate ?? null;
        const activeFlowRunId =
          typeof flowRunData?.flow_run_id === 'number'
            ? flowRunData.flow_run_id
            : typeof flowRunData?.id === 'number'
              ? flowRunData.id
              : typeof initialFlowRunId === 'number'
                ? initialFlowRunId
                : null;
        const flowQueryParam = remoteFlow?.id ?? flowIdentifier;

        const responseParams: Record<string, unknown> = {};
        if (typeof activeFlowRunId === 'number') {
          responseParams.flow_run = activeFlowRunId;
        } else if (flowQueryParam !== undefined && flowQueryParam !== null) {
          responseParams.flow = flowQueryParam;
        }

        let rawResponses: any[] = [];
        if (Object.keys(responseParams).length > 0) {
          try {
            const responsesResponse = await responses.list(responseParams);
            if (isCancelled) {
              return;
            }
            rawResponses = Array.isArray(responsesResponse?.data) ? responsesResponse.data : [];
          } catch (responsesError) {
            console.error('Failed to load saved responses for flow progression', responsesError);
          }
        }

        setFlowState(remoteFlow ?? initialFlowRef.current ?? null);

        const questionList: Question[] = Array.isArray(questions?.data)
          ? questions.data
          : [];
        setQuestionsState(questionList);
        const flowItemMap: Record<number, number> = {};
        questionList.forEach((question) => {
          if (typeof question?.id === 'number' && typeof question?.flow_item_id === 'number') {
            flowItemMap[question.id] = question.flow_item_id;
          }
        });
        setFlowItemByQuestion(flowItemMap);
        const totalQuestions = questionList.length;
        const questionIds = questionList
          .map((question) => question.id)
          .filter((id): id is number => typeof id === 'number');

        const latestByQuestion: Record<number, StoredResponse> = {};
        rawResponses.forEach((raw) => {
          const mapped = mapApiResponseToStored(raw);
          if (!mapped || !questionIds.includes(mapped.question)) {
            return;
          }
          // rawResponses is sorted by -created_at (newest first).
          // Keep only the first (newest) entry per question.
          if (!(mapped.question in latestByQuestion)) {
            latestByQuestion[mapped.question] = mapped;
          }
        });
        setServerResponses(latestByQuestion);
        serverResponsesRef.current = latestByQuestion;

        const drafts: Record<number, FlowResponseDraft> = {};
        Object.keys(latestByQuestion).forEach((key) => {
          const questionId = Number(key);
          const stored = latestByQuestion[questionId];
          if (!stored) {
            return;
          }
          drafts[questionId] = storedToDraft(stored);
        });
        setResponseDrafts(drafts);

        const firstIncompleteIndex = findFirstIncompleteQuestionIndex(questionList, drafts);
        const derivedIndex = firstIncompleteIndex === -1 ? totalQuestions : firstIncompleteIndex;
        const derivedCompletedQuestionCount = countCompletedQuestions(questionList, drafts);

        const savedProgressRaw =
          typeof flowRunData?.current_index === 'number'
            ? flowRunData.current_index
            : typeof flowRunData?.current_question_index === 'number'
              ? flowRunData.current_question_index
              : null;
        const savedProgressIndex =
          savedProgressRaw !== null
            ? Math.min(Math.max(savedProgressRaw, 0), totalQuestions)
            : null;
        const defaultIndex =
          savedProgressIndex !== null ? Math.min(derivedIndex, savedProgressIndex) : derivedIndex;
        const fallbackFirstIncompleteIndex = firstIncompleteIndex === -1 ? null : firstIncompleteIndex;
        const nextFlowRunState = buildFlowRunState({
          flowRunData,
          totalQuestions,
          fallbackIndex: savedProgressIndex ?? defaultIndex,
          fallbackCompletedAt: flowRunData?.completed_at ?? null,
          fallbackStatus: (savedProgressIndex ?? derivedCompletedQuestionCount) > 0 ? 'in_progress' : 'not_started',
          fallbackFlowRunId: activeFlowRunId,
          fallbackTargetDate: resolvedTargetDate,
          fallbackCompletedQuestionCount: derivedCompletedQuestionCount,
          fallbackFirstIncompleteIndex,
        });
        const completionIndex = nextFlowRunState.status === 'completed'
          ? totalQuestions
          : nextFlowRunState.firstIncompleteIndex ?? defaultIndex;
        // Resume at the earliest unsatisfied question or completion screen when finished.
        setCurrentIndex(completionIndex);
        setFlowRunState(nextFlowRunState);
      } catch (initializationError) {
        if (isCancelled) {
          return;
        }

        const httpStatus = (initializationError as { response?: { status?: number } })?.response?.status;
        setIsAccessDenied(httpStatus === 403);
        setError(getErrorMessage(initializationError));
        setFlowState(initialFlowRef.current ?? null);
        setQuestionsState([]);
        setServerResponses({});
        serverResponsesRef.current = {};
        setResponseDrafts({});
        setCurrentIndex(0);
        setFlowRunState({
          currentIndex: 0,
          totalQuestions: 0,
          completedAt: null,
          status: 'not_started',
          flowRunId: initialFlowRunId ?? null,
          targetDate: targetDate ?? null,
          progressPercent: 0,
          completedQuestionCount: 0,
          isFullyAnswered: false,
          firstIncompleteIndex: null,
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    initialize();

    return () => {
      isCancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, flowName, hydratedBootstrapState, initialFlowRunId, targetDate, user?.id]);

  useEffect(() => {
    if (initialFlow && !hydratedBootstrapState) {
      setFlowState(initialFlow);
    }
  }, [hydratedBootstrapState, initialFlow]);

  useEffect(() => {
      setFlowRunState((previous) =>
        previous.totalQuestions === questionsState.length
          ? previous
          : {
            ...previous,
            totalQuestions: questionsState.length,
            progressPercent: previous.completedQuestionCount > 0 || questionsState.length === 0
              ? deriveProgressPercent(previous.completedQuestionCount, questionsState.length)
              : previous.progressPercent,
          },
    );
  }, [questionsState.length]);

  const persistResponsePayload = useCallback(
    async (
      questionId: number,
      payload: ResponsePayload,
      existing?: StoredResponse,
      context?: CapturedCourseContext,
      owner?: SaveScope,
    ): Promise<StoredResponse | null> => {
      const onError = (failure: unknown): never => {
        if (owner && owner !== scopeRef.current) throw failure;
        return handleRuntimeError(failure);
      };
      if (existing) {
        const updateResponse = await (context ? responses.update(existing.id, payload, context) : responses.update(existing.id, payload)).catch(onError);
        const stored = mapApiResponseToStored(updateResponse.data) ?? {
          ...existing,
          ...payload,
        };

        return stored;
      }

      const createResponse = await (context ? responses.create(payload, context) : responses.create(payload)).catch(onError);
      const stored = mapApiResponseToStored(createResponse.data);

      if (stored) {
        return stored;
      }

      const fallbackId = typeof createResponse.data?.id === 'number' ? createResponse.data.id : undefined;
      if (typeof fallbackId === 'number') {
        return {
          id: fallbackId,
          question: questionId,
          flow_item: payload.flow_item,
          flow_run: payload.flow_run,
          generation_id: payload.generation_id,
          is_completed: typeof payload.is_completed === 'boolean' ? payload.is_completed : false,
          rating_value: payload.rating_value,
          text_value: payload.text_value,
          boolean_value: payload.boolean_value,
          selected_options: payload.selected_options,
          revealed_options: payload.revealed_options,
          custom_other_value: payload.custom_other_value,
          audio_url: payload.audio_url,
          text_list: payload.text_list,
          metadata: cloneMetadata(payload.metadata),
        };
      }

      return null;
    },
    [handleRuntimeError],
  );

  // Adopt responses the server recorded on the learner's behalf (for example a
  // won roleplay step) so the flow treats them as saved and later persists update
  // that row instead of creating a duplicate.
  const adoptServerResponses = useCallback(
    (rawResponses: unknown[]) => {
      const knownQuestionIds = new Set(
        questionsState
          .map((question) => question.id)
          .filter((id): id is number => typeof id === 'number'),
      );
      const adopted: Record<number, StoredResponse> = {};
      rawResponses.forEach((raw) => {
        const mapped = mapApiResponseToStored(raw);
        if (!mapped || !knownQuestionIds.has(mapped.question)) {
          return;
        }
        const current = adopted[mapped.question];
        if (!current || mapped.id > current.id) {
          adopted[mapped.question] = mapped;
        }
      });

      const adoptedQuestionIds = Object.keys(adopted).map(Number);
      if (!adoptedQuestionIds.length) {
        return;
      }

      const nextServerResponses = { ...serverResponsesRef.current, ...adopted };
      serverResponsesRef.current = nextServerResponses;
      setServerResponses(nextServerResponses);

      const nextDrafts: Record<number, FlowResponseDraft> = {};
      adoptedQuestionIds.forEach((questionId) => {
        nextDrafts[questionId] = storedToDraft(adopted[questionId]);
      });
      responseDraftsRef.current = { ...responseDraftsRef.current, ...nextDrafts };
      setResponseDrafts((previous) => ({ ...previous, ...nextDrafts }));
    },
    [questionsState, responseDraftsRef, serverResponsesRef, setResponseDrafts, setServerResponses],
  );

  // Cache the latest answer locally and advance progress as needed.
  const answerQuestion = useCallback(
    (questionId: number, response: ResponsePayload, options?: SubmitQuestionOptions) => {
      if (!questionId) {
        console.warn('[answerQuestion] no questionId');
        return;
      }
      draftRevisionRef.current += 1;
      const flowItemId =
        typeof response.flow_item === 'number' ? response.flow_item : flowItemByQuestion[questionId];
      const resolvedFlowRun =
        typeof response.flow_run === 'number' ? response.flow_run : flowRunState.flowRunId ?? null;

      setResponseDrafts((previous) => {
        const previousDraft = previous[questionId];
        const responseId = previousDraft?.responseId ?? serverResponses[questionId]?.id;
        const selected = Array.isArray(response.selected_options)
          ? [...response.selected_options]
          : response.selected_options;
        const revealed = Array.isArray(response.revealed_options)
          ? [...response.revealed_options]
          : response.revealed_options;
        const nextFlowItem =
          typeof flowItemId === 'number' ? flowItemId : previousDraft?.flow_item ?? flowItemByQuestion[questionId];
        const nextFlowRun =
          typeof resolvedFlowRun === 'number'
            ? resolvedFlowRun
            : previousDraft?.flow_run ?? flowRunState.flowRunId ?? undefined;
        const existingResponse = serverResponses[questionId];
        const inferredCompletion =
          typeof response.is_completed === 'boolean'
            ? response.is_completed
            : previousDraft?.is_completed ?? existingResponse?.is_completed ?? false;
        const nextIsCompleted = options?.advance ? true : inferredCompletion;

        return {
          ...previous,
          [questionId]: {
            ...previousDraft,
            ...response,
            question: questionId,
            responseId,
            flow_item: nextFlowItem,
            flow_run: nextFlowRun,
            selected_options: selected,
            revealed_options: revealed,
            is_completed: nextIsCompleted,
          },
        };
      });

      const questionIndex = questionsState.findIndex((question) => question.id === questionId);
      if (options?.advance && questionIndex >= 0) {
        setCurrentIndex((previousIndex) => Math.min(Math.max(previousIndex, questionIndex + 1), questionsState.length));
      }
    },
    [questionsState, serverResponses, flowItemByQuestion, flowRunState.flowRunId],
  );

  // Sync drafts up to the target index with the backend and local cache.
  const performPersistResponses = useCallback(
    async (targetIndex: number, options?: { forceComplete?: boolean }) => {
      const owner = scopeRef.current;
      if (isFinishing()) return serverResponsesRef.current;
      const markComplete = options?.forceComplete ?? false;
      const boundedTarget = Math.min(Math.max(targetIndex, 0), questionsState.length);
      if (boundedTarget === 0) {
        return serverResponsesRef.current;
      }

      if (owner?.retrySubmission) {
        await reconcileBeforeAutosave(owner);
        if (owner !== scopeRef.current || isFinishing()) return serverResponsesRef.current;
      }

      const questionSubset = questionsState.slice(0, boundedTarget);
      const nextServerResponses: Record<number, StoredResponse> = { ...serverResponsesRef.current };
      // Read from the ref to get the latest drafts, avoiding stale closure
      // values when called from a chained promise (e.g. submitFlow after
      // submitQuestionResponse).
      const currentDrafts = responseDraftsRef.current;
      const draftUpdates: Record<number, FlowResponseDraft> = {};

      const flowItems = flowItemByQuestion;
      const flowRunId = flowRunState.flowRunId;

      for (const question of questionSubset) {
        if (owner !== scopeRef.current || isFinishing()) break;
        const questionId = question.id;
        if (!questionId) {
          continue;
        }

        // Evaluated answers are completed and assembled only by their endpoint.
        // Never force-complete or reconstruct them from the original form input.
        if (hasAnswerEvaluation(question)) {
          continue;
        }

        const normalizedQuestionType = normalizeQuestionType(
          question.question_type_display ?? question.question_type ?? '',
        );

        const existingDraft = currentDrafts[questionId];
        if (!existingDraft) {
          continue;
        }

        const { workingDraft, shouldForceComplete: forceCompleteDraft } = getWorkingDraft(
          question,
          existingDraft,
          markComplete,
        );

        if (shouldDeferIncompleteCustomOtherPersist(question, workingDraft)) {
          continue;
        }

        const payload = buildRequestPayload(workingDraft, questionId, flowItems[questionId], flowRunId);
        const existing = serverResponsesRef.current[questionId];

        if (shouldSkipPersist(payload, existing, normalizedQuestionType)) {
          draftUpdates[questionId] = {
            ...workingDraft,
            responseId: existing.id,
          };
          continue;
        }

        try {
          const stored = await persistResponsePayload(questionId, payload, existing);
          if (!stored) {
            draftUpdates[questionId] = workingDraft;
            continue;
          }

          const { stored: storedForServer, draft: storedDraft } = applyStoredResponse(
            stored,
            workingDraft,
            forceCompleteDraft,
          );

          nextServerResponses[questionId] = storedForServer;
          draftUpdates[questionId] = storedDraft;
        } catch (persistenceError) {
          throw persistenceError;
        }
      }

      if (owner !== scopeRef.current) return serverResponsesRef.current;
      const savedResponses = Object.fromEntries(Object.keys(draftUpdates).map((id) => [id, nextServerResponses[Number(id)]]).filter(([, value]) => value));
      serverResponsesRef.current = { ...serverResponsesRef.current, ...savedResponses };
      setServerResponses(serverResponsesRef.current);
      // Use a functional updater so we merge into the latest state rather
      // than replacing it with a potentially stale snapshot, which would
      // clobber drafts added by concurrent answerQuestion calls.
      setResponseDrafts((prev) => {
        const next = { ...prev };
        Object.entries(draftUpdates).forEach(([key, draft]) => {
          const id = Number(key);
          // A background save must never overwrite input entered after it began.
          if (prev[id] === currentDrafts[id]) next[id] = draft;
          else if (prev[id]) next[id] = { ...prev[id], responseId: draft.responseId };
        });
        return next;
      });

      return nextServerResponses;
    },
    [questionsState, flowItemByQuestion, flowRunState.flowRunId, persistResponsePayload, responseDraftsRef, serverResponsesRef, isFinishing],
  );


  const persistResponses = useCallback((targetIndex: number, options?: { forceComplete?: boolean }) => {
    const work = performPersistResponses(targetIndex, options);
    pendingBulkSavesRef.current.add(work);
    void work.then(() => pendingBulkSavesRef.current.delete(work), () => pendingBulkSavesRef.current.delete(work));
    return work;
  }, [performPersistResponses]);

  const registerBackgroundSave = useCallback((questionId: number, promise: Promise<void>) => {
    const owner = scopeRef.current;
    backgroundSavesRef.current[questionId] = promise;
    const clearPendingSave = () => {
      if (backgroundSavesRef.current[questionId] === promise) {
        delete backgroundSavesRef.current[questionId];
      }
    };
    // Keep the original rejection for its caller without creating an unhandled
    // rejected promise from a detached finally cleanup.
    void promise.then(() => {
      const wasPending = backgroundSavesRef.current[questionId] === promise;
      clearPendingSave();
      if (owner !== scopeRef.current || !wasPending) return;
      failedBackgroundSavesRef.current.delete(questionId);
      setPersistedRevisions((previous) => ({ ...previous, [questionId]: (previous[questionId] ?? 0) + 1 }));
    }, () => {
      clearPendingSave();
      if (owner !== scopeRef.current) return;
      failedBackgroundSavesRef.current.add(questionId);
    });
  }, []);

  const captureAnswerSave = useCallback((questionId: number, override?: ResponsePayload): AnswerSaveSnapshot | null => {
    const owner = scopeRef.current;
    if (!owner) return null;
    const question = questionsState.find((item) => item.id === questionId);
    const existing = serverResponsesRef.current[questionId];
    if (hasAnswerEvaluation(question) && existing?.metadata?.ai_evaluation) return null;
    const base = responseDraftsRef.current[questionId];
    const draft = override
      ? deriveDraftFromOverride(base, override, questionId, flowItemByQuestion[questionId], flowRunState.flowRunId)
      : base;
    if (!draft || (question && shouldDeferIncompleteCustomOtherPersist(question, draft))) return null;
    if (existing) owner.savedResponses[questionId] = existing;
    const snapshot = {
      owner, questionId,
      questionType: normalizeQuestionType(question?.question_type_display ?? question?.question_type ?? ''),
      payload: buildRequestPayload(draft, questionId, flowItemByQuestion[questionId], flowRunState.flowRunId),
      context: courseId != null ? { courseId } : captureCourseRuntimeContext(),
    };
    owner.pendingAnswers.set(questionId, snapshot);
    return snapshot;
  }, [courseId, flowItemByQuestion, flowRunState.flowRunId, questionsState, responseDraftsRef, serverResponsesRef]);

  const performExecutePersist = useCallback(async (snapshot: AnswerSaveSnapshot, previousSave?: Promise<void>) => {
    const { owner, questionId, payload, questionType, context } = snapshot;
    if (previousSave) await previousSave.catch(() => undefined);
    // Route changes may flush the old module. Authentication changes must never
    // send its queued answers using the next person's credentials.
    if (owner.userId !== currentUserIdRef.current || owner.authGeneration !== getAuthGeneration()) return;
    await reconcileBeforeAutosave(owner);
    if (owner.userId !== currentUserIdRef.current || owner.authGeneration !== getAuthGeneration()) return;
    const existing = owner.savedResponses[questionId];
    const stored = shouldSkipPersist(payload, existing, questionType) ? existing
      : await persistResponsePayload(questionId, payload, existing, context, owner);
    if (!stored) return;
    owner.savedResponses[questionId] = stored;
    if (owner.pendingAnswers.get(questionId) === snapshot) owner.pendingAnswers.delete(questionId);
    if (owner !== scopeRef.current) return;
    const nextResponses = { ...serverResponsesRef.current, [questionId]: stored };
    serverResponsesRef.current = nextResponses;
    setServerResponses(nextResponses);
    setResponseDrafts((previous) => previous[questionId]
      ? { ...previous, [questionId]: { ...previous[questionId], responseId: stored.id } }
      : previous);
  }, [persistResponsePayload, serverResponsesRef]);

  const executePersist = useCallback((...args: Parameters<typeof performExecutePersist>) => {
    const work = performExecutePersist(...args);
    pendingSaveWritesRef.current.add(work);
    void work.then(() => pendingSaveWritesRef.current.delete(work), () => pendingSaveWritesRef.current.delete(work));
    return work;
  }, [performExecutePersist]);

  const runDebouncedEntry = useCallback(
    (questionId: number, entry: DebouncedSaveEntry) => {
      entry.timeoutId = null;

      executePersist(entry.snapshot, entry.previousSave)
        .then(entry.resolve)
        .catch((error) => {
          entry.reject(error);
        })
        .finally(() => {
          if (debouncedSavesRef.current[questionId] === entry) {
            delete debouncedSavesRef.current[questionId];
          }
        });
    },
    [executePersist],
  );

  const persistQuestionResponse = useCallback(
    (questionId: number, payloadOverride?: ResponsePayload, options?: { immediate?: boolean }) => {
      if (!questionId) return;
      const snapshot = captureAnswerSave(questionId, payloadOverride);
      if (!snapshot || isFinishing()) return;
      const immediate = options?.immediate ?? false;

      if (immediate) {
        const existingEntry = debouncedSavesRef.current[questionId];
        if (existingEntry) {
          if (existingEntry.timeoutId !== null) {
            // Timer hasn't fired yet — safe to clear and run immediately.
            clearTimeout(existingEntry.timeoutId);
            existingEntry.timeoutId = null;
            existingEntry.snapshot = snapshot;
            runDebouncedEntry(questionId, existingEntry);
            return existingEntry.promise;
          }

          // Timer already fired (executePersist is in-flight). Chain a new
          // persist after the in-flight one completes so that
          // serverResponsesRef is populated and we issue an update, not a
          // duplicate create.
          const previousSave = existingEntry.promise;
          const runPromise = executePersist(snapshot, previousSave);
          registerBackgroundSave(questionId, runPromise);
          return runPromise;
        }

        const previousSave = backgroundSavesRef.current[questionId];
        const runPromise = executePersist(snapshot, previousSave);
        registerBackgroundSave(questionId, runPromise);
        return runPromise;
      }

      const existingEntry = debouncedSavesRef.current[questionId];
      if (existingEntry && existingEntry.timeoutId !== null) {
        clearTimeout(existingEntry.timeoutId);
        existingEntry.snapshot = snapshot;
        existingEntry.timeoutId = setTimeout(
          () => runDebouncedEntry(questionId, existingEntry),
          AUTO_SAVE_DEBOUNCE_MS,
        );
        return existingEntry.promise;
      }

      const previousSave = backgroundSavesRef.current[questionId];
      let resolvePromise: () => void;
      let rejectPromise: (error: unknown) => void;
      const promise = new Promise<void>((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const entry: DebouncedSaveEntry = {
        timeoutId: null,
        promise,
        resolve: resolvePromise!,
        reject: rejectPromise!,
        snapshot,
        previousSave,
      };

      entry.timeoutId = setTimeout(() => runDebouncedEntry(questionId, entry), AUTO_SAVE_DEBOUNCE_MS);
      debouncedSavesRef.current[questionId] = entry;
      registerBackgroundSave(questionId, promise);

      return promise;
    },
    [captureAnswerSave, executePersist, registerBackgroundSave, runDebouncedEntry, isFinishing],
  );



  useEffect(() => {
    scopeRef.current = scope;
    setIsSubmitting(false);
    const failedSaves = failedBackgroundSavesRef.current;
    const pendingProgress = pendingProgressRef.current;
    const pendingBulkSaves = pendingBulkSavesRef.current;
    const pendingSaveWrites = pendingSaveWritesRef.current;
    return () => {
      if (scopeRef.current === scope) scopeRef.current = null;
      for (const entry of Object.values(debouncedSavesRef.current)) {
        if (entry.timeoutId !== null) { clearTimeout(entry.timeoutId); entry.resolve(); }
      }
      const finishing = submissionRef.current?.scope === scope ? submissionRef.current.promise : undefined;
      const started = [...pendingSaveWrites, ...pendingBulkSaves, ...(finishing ? [finishing] : [])];
      // A successful Finish consumes its snapshots; a failed/cancelled Finish
      // leaves them here to preserve answers when navigating away.
      void Promise.allSettled(started).then(async () => {
        if (scope.userId !== currentUserIdRef.current || scope.authGeneration !== getAuthGeneration()) return;
        if (scope.retrySubmission) {
          try { await reconcileBeforeAutosave(scope); }
          catch (failure) {
            console.error('Failed to retry module submission while leaving', failure);
            return; // An ambiguous batch must never become duplicate creates.
          }
        }
        for (const snapshot of scope.pendingAnswers.values()) {
          await performExecutePersist(snapshot).catch((failure) => console.error('Failed to save response while leaving module', failure));
        }
      });
      debouncedSavesRef.current = {};
      backgroundSavesRef.current = {};
      failedSaves.clear(); pendingProgress.clear(); pendingBulkSaves.clear(); pendingSaveWrites.clear();
    };
  }, [scope, performExecutePersist]);

  const waitForPendingBackgroundSaves = useCallback(
    async () => {
      const pendingEntries = Object.entries(debouncedSavesRef.current) as Array<[string, DebouncedSaveEntry]>;

      for (const [id, entry] of pendingEntries) {
        if (entry.timeoutId !== null) {
          clearTimeout(entry.timeoutId);
          runDebouncedEntry(Number(id), entry);
        }
      }

      const pending = Object.values(backgroundSavesRef.current);
      if (!pending.length) {
        return;
      }

      await Promise.allSettled(pending);
    },
    [runDebouncedEntry],
  );

  // Mirror local completion state to the server-side flow progress record.
  const performSyncProgress = useCallback(
    async (targetIndex: number, options?: { forceRefresh?: boolean }) => {
      const owner = scopeRef.current;
      if (isFinishing()) return;
      const effectiveFlowName = flowState?.name ?? flowName;
      if (!effectiveFlowName || !questionsState.length) {
        return;
      }

      const boundedTarget = Math.min(Math.max(targetIndex, 0), questionsState.length);

      if (options?.forceRefresh) {
        const targetQuestion = questionsState[Math.max(0, boundedTarget - 1)];
        if (typeof targetQuestion?.id !== 'number') {
          throw new Error('Your progress could not be checked. Reload this page to try again.');
        }
        const response = await flows.updateProgress(effectiveFlowName, {
          question_id: targetQuestion.id,
          current_index: boundedTarget,
          flow_run_id: flowRunState.flowRunId ?? undefined,
          target_date: flowRunState.targetDate ?? targetDate ?? undefined,
        }).catch(handleRuntimeError);
        const nextState = buildFlowRunState({
          flowRunData: response?.data ?? {},
          totalQuestions: questionsState.length,
          fallbackIndex: flowRunState.currentIndex,
          fallbackCompletedAt: flowRunState.completedAt,
          fallbackStatus: flowRunState.status,
          fallbackFlowRunId: flowRunState.flowRunId,
          fallbackTargetDate: flowRunState.targetDate ?? targetDate ?? null,
          fallbackCompletedQuestionCount: flowRunState.completedQuestionCount,
          fallbackFirstIncompleteIndex: flowRunState.firstIncompleteIndex,
        });
        if (owner === scopeRef.current) setFlowRunState(nextState);
        return nextState;
      }

      const flowRunCompleted = flowRunState.status === 'completed' || Boolean(flowRunState.completedAt);
      if (flowRunCompleted) {
        const nextState = buildFlowRunState({
          flowRunData: {
            current_question_index: Math.max(flowRunState.currentIndex, boundedTarget, questionsState.length),
            completed_at: flowRunState.completedAt,
            status: 'completed',
            flow_run_id: flowRunState.flowRunId,
            target_date: flowRunState.targetDate,
            completed_question_count: flowRunState.completedQuestionCount,
            progress_percent: flowRunState.progressPercent,
            is_fully_answered: true,
            first_incomplete_index: null,
          },
          totalQuestions: questionsState.length,
          fallbackIndex: flowRunState.currentIndex,
          fallbackCompletedAt: flowRunState.completedAt,
          fallbackStatus: 'completed',
          fallbackFlowRunId: flowRunState.flowRunId,
          fallbackTargetDate: flowRunState.targetDate ?? targetDate ?? null,
          fallbackCompletedQuestionCount: flowRunState.completedQuestionCount,
          fallbackFirstIncompleteIndex: flowRunState.firstIncompleteIndex,
        });
        if (owner === scopeRef.current) setFlowRunState(nextState);
        return nextState;
      }

      if (boundedTarget === flowRunState.currentIndex) {
        return flowRunState;
      }

      if (boundedTarget < flowRunState.currentIndex) {
        const safeIndex = Math.max(Math.min(boundedTarget, questionsState.length - 1), 0);
        const targetQuestion = questionsState[safeIndex];
        const targetQuestionId = targetQuestion?.id;

        if (!targetQuestionId) {
          const nextState = buildFlowRunState({
            flowRunData: {
              current_question_index: boundedTarget,
              completed_at: boundedTarget >= questionsState.length ? flowRunState.completedAt : null,
              status: boundedTarget >= questionsState.length ? 'completed' : 'in_progress',
              flow_run_id: flowRunState.flowRunId,
              target_date: flowRunState.targetDate,
              completed_question_count: flowRunState.completedQuestionCount,
              progress_percent: flowRunState.progressPercent,
              is_fully_answered: flowRunState.isFullyAnswered,
              first_incomplete_index: flowRunState.firstIncompleteIndex,
            },
            totalQuestions: questionsState.length,
            fallbackIndex: boundedTarget,
            fallbackCompletedAt: boundedTarget >= questionsState.length ? flowRunState.completedAt : null,
            fallbackStatus: boundedTarget >= questionsState.length ? 'completed' : 'in_progress',
            fallbackFlowRunId: flowRunState.flowRunId,
            fallbackTargetDate: flowRunState.targetDate ?? targetDate ?? null,
            fallbackCompletedQuestionCount: flowRunState.completedQuestionCount,
            fallbackFirstIncompleteIndex: flowRunState.firstIncompleteIndex,
          });
          if (owner === scopeRef.current) setFlowRunState(nextState);
          return nextState;
        }

        const response = await flows.updateProgress(effectiveFlowName, {
          question_id: targetQuestionId,
          current_index: boundedTarget,
          flow_run_id: flowRunState.flowRunId ?? undefined,
          target_date: flowRunState.targetDate ?? targetDate ?? undefined,
        }).catch(handleRuntimeError);
        const responseData = response?.data ?? {};
        const nextState = buildFlowRunState({
          flowRunData: responseData,
          totalQuestions: questionsState.length,
          fallbackIndex: boundedTarget,
          fallbackCompletedAt: boundedTarget >= questionsState.length ? flowRunState.completedAt : null,
          fallbackStatus: boundedTarget >= questionsState.length ? 'completed' : 'in_progress',
          fallbackFlowRunId: flowRunState.flowRunId,
          fallbackTargetDate: flowRunState.targetDate ?? targetDate ?? null,
          fallbackCompletedQuestionCount: flowRunState.completedQuestionCount,
          fallbackFirstIncompleteIndex: flowRunState.firstIncompleteIndex,
        });

        if (owner === scopeRef.current) setFlowRunState(nextState);
        return nextState;
      }

      let latestIndex = flowRunState.currentIndex;
      let completedAt = flowRunState.completedAt;
      let status: FlowRunStatus = flowRunState.status;
      let flowRunId = flowRunState.flowRunId;
      let nextTargetDate = flowRunState.targetDate ?? targetDate ?? null;
      let completedQuestionCount = flowRunState.completedQuestionCount;
      let progressPercent = flowRunState.progressPercent;
      let isFullyAnswered = flowRunState.isFullyAnswered;
      let firstIncompleteIndex = flowRunState.firstIncompleteIndex;

      for (let index = flowRunState.currentIndex; index < boundedTarget; index += 1) {
        if (owner !== scopeRef.current || isFinishing()) break;
        const question = questionsState[index];
        const questionId = question?.id;
        if (!questionId) {
          continue;
        }

        const response = await flows.updateProgress(effectiveFlowName, {
          question_id: questionId,
          current_index: index + 1,
          flow_run_id: flowRunState.flowRunId ?? undefined,
          target_date: flowRunState.targetDate ?? targetDate ?? undefined,
        }).catch(handleRuntimeError);
        const responseData = response?.data ?? {};
        const nextState = buildFlowRunState({
          flowRunData: responseData,
          totalQuestions: questionsState.length,
          fallbackIndex: latestIndex + 1,
          fallbackCompletedAt: completedAt,
          fallbackStatus: status === 'completed' ? 'completed' : 'in_progress',
          fallbackFlowRunId: flowRunId,
          fallbackTargetDate: nextTargetDate,
          fallbackCompletedQuestionCount: completedQuestionCount,
          fallbackFirstIncompleteIndex: firstIncompleteIndex,
        });

        latestIndex = nextState.currentIndex;
        completedAt = nextState.completedAt;
        status = nextState.status;
        flowRunId = nextState.flowRunId;
        nextTargetDate = nextState.targetDate;
        completedQuestionCount = nextState.completedQuestionCount;
        progressPercent = nextState.progressPercent;
        isFullyAnswered = nextState.isFullyAnswered;
        firstIncompleteIndex = nextState.firstIncompleteIndex;
      }

      const nextState = {
        currentIndex: Math.min(latestIndex, questionsState.length),
        totalQuestions: questionsState.length,
        completedAt,
        status,
        flowRunId: typeof flowRunId === 'number' ? flowRunId : null,
        targetDate: nextTargetDate,
        completedQuestionCount,
        progressPercent,
        isFullyAnswered,
        firstIncompleteIndex,
      };
      if (owner === scopeRef.current) setFlowRunState(nextState);
      return nextState;
    },
    [flowState, flowName, flowRunState, questionsState, targetDate, handleRuntimeError, isFinishing],
  );


  const syncProgress = useCallback((targetIndex: number, options?: { forceRefresh?: boolean }) => {
    const work = performSyncProgress(targetIndex, options);
    pendingProgressRef.current.add(work);
    void work.then(() => pendingProgressRef.current.delete(work), () => pendingProgressRef.current.delete(work));
    return work;
  }, [performSyncProgress]);

  // Step through questions while ensuring current progress is persisted.
  const goToNextQuestion = useCallback(() => {
    setCurrentIndex((previous) => Math.min(previous + 1, questionsState.length));
  }, [questionsState.length]);

  const goToPreviousQuestion = useCallback(() => {
    const previousIndex = currentIndex;
    const targetIndex = Math.max(previousIndex - 1, 0);

    if (previousIndex === targetIndex) {
      setCurrentIndex(targetIndex);
      return;
    }

    const safeIndex = Math.max(Math.min(previousIndex, questionsState.length - 1), 0);
    const currentQuestion = questionsState[safeIndex];

    const persistencePromise = currentQuestion?.id
      ? persistQuestionResponse(currentQuestion.id, undefined, { immediate: true })
      : undefined;

    if (persistencePromise) {
      persistencePromise.catch((error) => {
        console.error('Failed to persist current response before moving to previous question', error);
      });
    }

    persistResponses(previousIndex).catch((error) => {
      console.error('Failed to save responses before moving to previous question', error);
    });

    syncProgress(targetIndex).catch((error) => {
      console.error('Failed to sync progress before moving to previous question', error);
    });

    setCurrentIndex(targetIndex);
  }, [currentIndex, persistQuestionResponse, persistResponses, questionsState, syncProgress]);

  const goToQuestion = useCallback(
    (index: number) => {
      if (Number.isNaN(index)) {
        return;
      }

      setCurrentIndex(Math.min(Math.max(index, 0), questionsState.length));
    },
    [questionsState.length],
  );



  /**
   * REQ-106 save barrier before opening a question that quotes earlier answers:
   * flush pending background saves and retry failed ones. A retry failure is
   * reported as an earlier-answer error for that question, never as a conflict
   * on the question being continued.
   */
  const ensureEarlierAnswersSaved = useCallback(async () => {
    await waitForPendingBackgroundSaves();
    for (const failedId of Array.from(failedBackgroundSavesRef.current)) {
      const draft = responseDraftsRef.current[failedId];
      if (!draft) { failedBackgroundSavesRef.current.delete(failedId); continue; }
      try {
        await (persistQuestionResponse(failedId, draft, { immediate: true }) ?? Promise.resolve());
      } catch {
        throw Object.assign(new Error('An earlier answer could not be saved. Go back to it and try again.'), {
          code: 'earlier_answer_unsaved', questionId: failedId,
        });
      }
      failedBackgroundSavesRef.current.delete(failedId);
    }
  }, [persistQuestionResponse, responseDraftsRef, waitForPendingBackgroundSaves]);

  const submitQuestionResponse = useCallback(
    (response: ResponsePayload, options?: SubmitQuestionOptions) => {
      const questionId = response?.question;
      if (!questionId) {
        return Promise.resolve();
      }
      const fallbackFlowItem = flowItemByQuestion[questionId];
      const normalizedResponse: ResponsePayload = {
        ...response,
        flow_item:
          typeof response.flow_item === 'number'
            ? response.flow_item
            : typeof fallbackFlowItem === 'number'
              ? fallbackFlowItem
              : response.flow_item,
        flow_run:
          typeof response.flow_run === 'number'
            ? response.flow_run
            : typeof flowRunState.flowRunId === 'number'
              ? flowRunState.flowRunId
              : response.flow_run,
      };
      if (options?.advance) {
        normalizedResponse.is_completed = true;
      } else if (typeof response.is_completed === 'boolean') {
        normalizedResponse.is_completed = response.is_completed;
      }

      const owner = scopeRef.current;
      const shouldAdvance = options?.advance ?? false;
      const questionIndex = questionsState.findIndex((question) => question.id === questionId);
      if (hasAnswerEvaluation(questionsState[questionIndex]) && options?.serverPersisted) {
        if (response.is_completed !== true) {
          return Promise.reject(new Error('Finish this reflection before continuing.'));
        }
        adoptServerResponses([response]);
        if (!shouldAdvance) return Promise.resolve();
        const nextIndex = questionIndex + 1;
        // REQ-106: earlier answers the next card quotes must be saved first.
        const refreshProgress = () => owner === scopeRef.current ? syncProgress(nextIndex, { forceRefresh: true }) : Promise.resolve(undefined);
        // Resolve server progress before advancing; an old approval cannot move
        // the learner past a reflection changed in another tab.
        return (hasAnswerReferences(questionsState[nextIndex])
          ? ensureEarlierAnswersSaved().then(refreshProgress) : refreshProgress()).then((nextProgress) => {
          if (owner !== scopeRef.current || isFinishing()) return;
          const allowedIndex = nextProgress?.firstIncompleteIndex;
          if (allowedIndex != null && allowedIndex < nextIndex) {
            setCurrentIndex(allowedIndex);
            throw Object.assign(new Error('This reflection changed. Reload its saved answers before continuing.'), { code: 'answer_evaluation_stale' });
          }
          setCurrentIndex(nextIndex);
        });
      }
      if (hasAnswerEvaluation(questionsState[questionIndex])) {
        normalizedResponse.is_completed = false;
        if (shouldAdvance) return Promise.reject(new Error('Finish this reflection before continuing.'));
      }
      const nextQuestion = questionIndex >= 0 ? questionsState[questionIndex + 1] : undefined;
      // A generated step reads the saved answers when it mounts, so it must not appear before
      // the answer it depends on has actually been persisted.
      const nextGeneratesFromAnswers =
        shouldAdvance &&
        Boolean(nextQuestion) &&
        (hasQuestionGeneration(nextQuestion) || GENERATED_QUESTION_TYPES.has(
          normalizeQuestionType(nextQuestion?.question_type_display ?? nextQuestion?.question_type ?? ''),
        ));
      // REQ-106: a question showing earlier answers reads them when it mounts.
      const nextReadsAnswers = shouldAdvance && Boolean(nextQuestion) && hasAnswerReferences(nextQuestion);
      const mustPersistBeforeAdvance = nextGeneratesFromAnswers || nextReadsAnswers
        || (shouldAdvance && hasQuestionGeneration(questionsState[questionIndex]));

      answerQuestion(
        questionId,
        normalizedResponse,
        mustPersistBeforeAdvance ? { ...options, advance: false } : options,
      );
      const persistImmediately = options?.persistImmediately ?? shouldAdvance;
      const ownPersistence =
        persistQuestionResponse(questionId, normalizedResponse, { immediate: persistImmediately }) ??
        Promise.resolve();
      // Earlier answers saved in the background must also land before references are read.
      const persistencePromise = nextReadsAnswers
        ? ownPersistence.then(ensureEarlierAnswersSaved)
        : ownPersistence;

      if (!shouldAdvance) {
        return persistencePromise;
      }

      const nextIndex = questionIndex >= 0 ? questionIndex + 1 : flowRunState.currentIndex + 1;

      if (mustPersistBeforeAdvance) {
        const advance = () => {
          if (owner !== scopeRef.current || isFinishing()) return;
          if (options?.isCurrentSubmission && !options.isCurrentSubmission()) {
            throw Object.assign(new Error('Your answer changed while saving. Continue again with your latest answer.'), { code: 'answer_changed' });
          }
          setCurrentIndex((previousIndex) =>
            Math.min(Math.max(previousIndex, nextIndex), questionsState.length),
          );
        };
        return persistencePromise
          .then(advance, (persistError) => {
            // A question quoting this answer must not open before the answer is saved.
            if (!nextReadsAnswers && !hasQuestionGeneration(nextQuestion) && !hasQuestionGeneration(questionsState[questionIndex])) advance();
            throw persistError;
          })
          .then(() => owner === scopeRef.current ? syncProgress(nextIndex) : undefined);
      }

      return persistencePromise.then(() => owner === scopeRef.current ? syncProgress(nextIndex) : undefined).catch((failure) => {
        if (owner === scopeRef.current && !isFinishing() && isQuestionGenerationConflict(failure) && questionIndex >= 0) setCurrentIndex(questionIndex);
        throw failure;
      });
    },
    [
      answerQuestion,
      adoptServerResponses,
      persistQuestionResponse,
      flowItemByQuestion,
      flowRunState.currentIndex,
      flowRunState.flowRunId,
      questionsState,
      ensureEarlierAnswersSaved,
      syncProgress,
      isFinishing,
    ],
  );

  // Persist answered questions so the user can safely pause the flow.
  const saveProgress = useCallback(async () => {
    setIsSaving(true);
    setError(null);

    try {
      await waitForPendingBackgroundSaves();
      await persistResponses(currentIndex);
      await syncProgress(currentIndex);
    } catch (savingError) {
      const message = getErrorMessage(savingError);
      setError(message);
      throw savingError;
    } finally {
      setIsSaving(false);
    }
  }, [currentIndex, persistResponses, syncProgress, waitForPendingBackgroundSaves]);

  // Finish owns pending drafts until the atomic server submission settles. Unsent
  // debounce entries join the batch; already-started writes/progress must drain.
  const submitFlow = useCallback((options: SubmitFlowOptions = {}): Promise<void> => {
    const owner = scopeRef.current;
    if (!owner) return Promise.reject(new Error('This module is no longer open.'));
    const active = submissionRef.current;
    if (active?.scope === owner) return active.promise;
    const draftRevision = draftRevisionRef.current;
    const finishSnapshots: AnswerSaveSnapshot[] = [];
    for (const question of questionsState) {
      if (!question.id || hasAnswerEvaluation(question)
          || normalizeQuestionType(question.question_type_display ?? question.question_type ?? '') === 'roleplay') continue;
      const draft = options.finalResponse?.question === question.id ? options.finalResponse : responseDraftsRef.current[question.id];
      if (!draft || areResponsesEqual(draft, serverResponsesRef.current[question.id], normalizeQuestionType(question.question_type_display ?? question.question_type ?? ''))) continue;
      const snapshot = captureAnswerSave(question.id, draft);
      if (snapshot) finishSnapshots.push(snapshot);
    }

    const work = Promise.resolve().then(async () => {
      const assertCurrent = () => {
        if (owner !== scopeRef.current) throw new Error('This module is no longer open.');
        if (draftRevision !== draftRevisionRef.current || (options.isCurrentSubmission && !options.isCurrentSubmission())) {
          throw Object.assign(new Error('Your answer changed while saving. Finish again with your latest answer.'), { code: 'answer_changed' });
        }
      };
      try {
        assertCurrent();
        setIsSubmitting(true);
        setError(null);
        for (const [id, entry] of Object.entries(debouncedSavesRef.current)) {
          if (entry.timeoutId !== null) {
            clearTimeout(entry.timeoutId);
            delete debouncedSavesRef.current[Number(id)];
            delete backgroundSavesRef.current[Number(id)];
            entry.resolve();
          }
        }
        await Promise.allSettled([
          ...Object.values(backgroundSavesRef.current),
          ...pendingBulkSavesRef.current,
          ...pendingSaveWritesRef.current,
          ...pendingProgressRef.current,
        ]);
        assertCurrent();
        // Resolve the previous operation before comparing newer drafts. It may
        // have committed even though its reply was lost; replacing its receipt
        // would leave this batch with obsolete IDs/versions indefinitely.
        if (owner.retrySubmission) {
          await owner.retrySubmission();
          assertCurrent();
          serverResponsesRef.current = { ...serverResponsesRef.current, ...owner.savedResponses };
          setServerResponses(serverResponsesRef.current);
          retryRef.current = null;
        }
        if (!flowRunState.flowRunId) throw new Error('Your module could not be submitted. Reload this page to try again.');
        const drafts = { ...responseDraftsRef.current };
        const final = options.finalResponse;
        if (final) {
          if (options.serverPersisted) {
            adoptServerResponses([final]);
          } else {
            drafts[final.question] = deriveDraftFromOverride(
              drafts[final.question], final, final.question,
              flowItemByQuestion[final.question], flowRunState.flowRunId,
            );
            responseDraftsRef.current = drafts;
            setResponseDrafts(drafts);
          }
        }
        const answers: FlowSubmissionRequest['answers'] = [];
        for (const question of questionsState) {
          if (!question.id || hasAnswerEvaluation(question)
              || normalizeQuestionType(question.question_type_display ?? question.question_type ?? '') === 'roleplay') continue;
          const draft = drafts[question.id];
          if (!draft) continue;
          const { workingDraft } = getWorkingDraft(question, draft, true);
          if (shouldDeferIncompleteCustomOtherPersist(question, workingDraft)) {
            throw new Error('Complete the Other answer before finishing this module.');
          }
          const existing = serverResponsesRef.current[question.id];
          const type = normalizeQuestionType(question.question_type_display ?? question.question_type ?? '');
          // A resumed Voice answer keeps its canonical provenance. Compare
          // before building a Standard write, which intentionally drops it.
          if (existing && areResponsesEqual(workingDraft, existing, type)) continue;
          const payload = buildRequestPayload(workingDraft, question.id, flowItemByQuestion[question.id], flowRunState.flowRunId);
          if (existing && areResponsesEqual(payload, existing, type)) continue;
          // AI generation provenance is server-owned. generation_id separately
          // binds the answer to the exact personalized wording that was shown.
          if (payload.metadata && Object.prototype.hasOwnProperty.call(payload.metadata, 'ai_generation')) {
            const { ai_generation, ...metadata } = payload.metadata;
            payload.metadata = metadata;
          }
          if (existing && !existing.updated_at) throw new Error('Reload this module before finishing so its saved answers can be checked.');
          answers.push({ response_id: existing?.id ?? null, expected_updated_at: existing?.updated_at ?? null, response: payload });
        }
        const signature = JSON.stringify({ flow_run_id: flowRunState.flowRunId, answers });
        const previous = retryRef.current;
        const request = previous?.scope === owner && previous.signature === signature
          ? previous.request
          : { ...JSON.parse(signature), request_id: newSubmissionId() } as FlowSubmissionRequest;
        // Freeze exactly what went over the wire, retaining the operation ID on
        // an ambiguous network failure. A changed answer gets a new operation.
        retryRef.current = { scope: owner, signature, request };
        const context = courseId != null ? { courseId } : captureCourseRuntimeContext();
        const applyReceipt = (data: FlowSubmissionResult) => {
          if (data.progress.flow_run_id !== request.flow_run_id) throw new Error('The returned module progress did not match this session.');
          for (const raw of data.responses) {
            const stored = mapApiResponseToStored(raw);
            if (stored) owner.savedResponses[stored.question] = stored;
          }
          // Saved row versions remain useful even when newer input prevents
          // this receipt from advancing the UI. Do not replace those drafts.
          if (owner === scopeRef.current) {
            serverResponsesRef.current = { ...serverResponsesRef.current, ...owner.savedResponses };
            setServerResponses(serverResponsesRef.current);
          }
          for (const snapshot of finishSnapshots) {
            if (owner.pendingAnswers.get(snapshot.questionId) === snapshot) owner.pendingAnswers.delete(snapshot.questionId);
          }
          owner.retrySubmission = undefined;
        };
        const send = () => flows.submit(flowState?.name ?? flowName, request, context).catch((failure) => {
          const status = failure?.response?.status;
          if (typeof status === 'number' && status >= 400 && status < 500) {
            owner.retrySubmission = undefined;
            if (retryRef.current?.request === request) retryRef.current = null;
          }
          throw failure;
        });
        owner.retrySubmission = async () => { applyReceipt((await send()).data); };
        const result = await send().catch((failure) => {
          if (owner !== scopeRef.current) throw failure;
          return handleRuntimeError(failure);
        });
        const data = result.data;
        applyReceipt(data);
        assertCurrent();
        adoptServerResponses(data.responses);
        const nextState = buildFlowRunState({
          flowRunData: data.progress, totalQuestions: questionsState.length,
          fallbackIndex: currentIndex, fallbackCompletedAt: null, fallbackStatus: 'in_progress',
          fallbackFlowRunId: flowRunState.flowRunId, fallbackTargetDate: flowRunState.targetDate,
          fallbackCompletedQuestionCount: 0, fallbackFirstIncompleteIndex: 0,
        });
        // Completion authority is the explicit server status, never a client
        // answer count or a force-completed draft.
        const completed = data.progress.status === 'completed' && Boolean(data.progress.completed_at);
        nextState.status = completed ? 'completed' : 'in_progress';
        nextState.completedAt = completed ? data.progress.completed_at ?? null : null;
        nextState.currentIndex = completed ? questionsState.length : data.progress.first_incomplete_index ?? currentIndex;
        setFlowRunState(nextState);
        setCurrentIndex(nextState.currentIndex);
        retryRef.current = null;
        if (!completed) {
          if (questionsState.some(hasAnswerEvaluation)) {
            throw Object.assign(new Error('A reflection needs attention before this module can be completed.'),
              { code: 'answer_evaluation_stale' });
          }
          throw new Error('A question needs attention before this module can be completed.');
        }
        setSubmissionState({ scope: owner, result: data });
      } catch (submissionError) {
        if (owner === scopeRef.current && !options.preserveQuestionOnError) setError(getErrorMessage(submissionError));
        throw submissionError;
      } finally {
        if (submissionRef.current?.promise === work) submissionRef.current = null;
        if (owner === scopeRef.current) setIsSubmitting(false);
      }
    });
    submissionRef.current = { scope: owner, promise: work };
    return work;
  }, [adoptServerResponses, captureAnswerSave, courseId, currentIndex, flowItemByQuestion, flowName, flowRunState.flowRunId,
    flowRunState.targetDate, flowState?.name, handleRuntimeError, questionsState, responseDraftsRef, serverResponsesRef]);

  // Expose responses in question order for review screens.
  const orderedResponses = useMemo(() =>
    questionsState
      .slice(0, currentIndex)
      .map((question) => (question.id ? responseDrafts[question.id] : undefined))
      .filter((response): response is FlowResponseDraft => Boolean(response)),
  [questionsState, currentIndex, responseDrafts]);

  // Detect whether any draft differs from what was last stored.
  const hasUnsavedChanges = useMemo(() => {
    return questionsState.some((question) => {
      const questionId = question.id;
      if (!questionId) {
        return false;
      }

      const draft = responseDrafts[questionId];
      if (!draft) {
        return false;
      }

      const existing = serverResponses[questionId];
      const normalizedQuestionType = normalizeQuestionType(
        question.question_type_display ?? question.question_type ?? '',
      );
      return !existing || !areResponsesEqual(draft, existing, normalizedQuestionType);
    });
  }, [questionsState, responseDrafts, serverResponses]);

  // Provide the question currently in focus, if any remain.
  const currentQuestion = useMemo(() => {
    if (currentIndex >= questionsState.length) {
      return null;
    }

    return questionsState[currentIndex];
  }, [currentIndex, questionsState]);

  const isFlowCompleted = flowRunState.status === 'completed' || Boolean(flowRunState.completedAt);

  // Check that every question has at least one draft response.
  const allQuestionsAnswered = useMemo(
    () =>
      questionsState.every((question) => {
        const questionId = question.id;
        if (!questionId) {
          return false;
        }

        const draft = responseDrafts[questionId];
        return Boolean(draft && draft.question === questionId);
      }),
    [questionsState, responseDrafts],
  );

  const canSubmit = !loading && !isFlowCompleted && questionsState.length > 0 && allQuestionsAnswered;

  return {
    loading,
    error,
    isAccessDenied,
    lockedCourseId,
    flow: flowState,
    questions: questionsState,
    currentQuestionIndex: currentIndex,
    currentQuestion,
    responsesByQuestion: responseDrafts,
    persistedRevisions,
    orderedResponses,
    hasUnsavedChanges,
    isSaving,
    isSubmitting,
    canSubmit,
    isFlowCompleted,
    progress: flowRunState,
    submitQuestionResponse,
    goToNextQuestion,
    goToPreviousQuestion,
    goToQuestion,
    saveProgress,
    submitFlow,
    submissionResult,
    adoptServerResponses,
  };
};




